/**
 * Vegetation.ts
 * -----------------------------------------------------------------------------
 * Trees and grass scattered over an imported map's ground, for the Modeler's 3D
 * view. Trees come from ez-tree (@dgreenheck/ez-tree); the grass is generated
 * here, because at site scale it is tufts rather than blades.
 *
 * WHAT THIS IS FOR
 * ================
 * Context, not survey data. Nothing here is read from the LandXML file and
 * nothing here is exported — it is scenery, so an engineer looking at a bridge on
 * a bare grey plane can see the site as a site. It is therefore kept out of
 * `bridge.root` entirely: it must not reach picking, selection, the Explorer, or
 * the bounds that Zoom All frames, or it would start behaving like model geometry.
 *
 * EVERYTHING IS INSTANCED, AND EVERYTHING IS SEEDED
 * =================================================
 * One ez-tree is 14–20k triangles, so the only way to have a hundred of them is
 * InstancedMesh — three variants, two meshes each (trunk, leaves), six draw calls
 * for the whole planting however many trees there are. Placement runs off a fixed
 * seed so the trees do not jump to new positions every time the scene is rebuilt;
 * a redraw after toggling the wireframe has to leave the site looking the same.
 */

import * as THREE from 'three';
import { Tree } from '@dgreenheck/ez-tree';

/** ez-tree presets used as the three variants. Mixed deliberately: one planting
 *  of a single species reads as a car park, not a landscape. */
const TREE_PRESETS = ['Ash Medium', 'Oak Medium', 'Pine Medium'] as const;

/** Target tree height in metres, before per-instance variation. The presets come
 *  out 50–77 units tall and the scene is in metres, so they are scaled to this
 *  rather than used at their own size — a 65 m tree dwarfs the bridge. */
const TREE_HEIGHT_M = 11;
const TREE_HEIGHT_VARIATION = 0.35;

/**
 * Planting rates at density 1.0, PER SQUARE METRE of plantable ground, with a
 * hard ceiling on each.
 *
 * Per-area rather than a flat count, because a flat count means two different
 * things: 400 trees across a 150 m test patch is a thicket, and across a 2 km
 * corridor it is four hundred specks. The rate keeps the look of the planting
 * constant and lets the ceiling — not the count — be what protects the frame rate
 * on a big site.
 *
 * A tuft is four triangles, so even the grass ceiling is under half a million in
 * one draw call. Trees are the expensive side: 400 × ~17k is around 7M, which
 * instancing draws in six calls but still has to transform.
 */
const TREES_PER_M2 = 1 / 220;
const GRASS_PER_M2 = 2;
const MAX_TREES = 400;
const MAX_GRASS = 120_000;

/** Tufts are two crossed quads; anything below this slope is plantable ground.
 *  cos(40°) — trees do not grow on the cut faces of an embankment, and putting
 *  them there is the thing that makes scattered scenery look wrong. */
const MAX_SLOPE_COS = Math.cos((40 * Math.PI) / 180);

/** Metres. Grass tuft footprint and height. */
const GRASS_W = 0.9;
const GRASS_H = 0.55;

export interface VegetationOptions {
  /** 0 = none, 1 = as thick as this will go. */
  density: number;
}

/** Deterministic, cheap, and good enough for scatter — mulberry32. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Spot {
  x: number; y: number; z: number;
}

interface Plantable {
  /** Running area total per triangle — one binary search per point places it
   *  correctly however uneven the triangulation is. */
  cum: number[];
  tri: Array<{ p: Float32Array; i: number }>;
  /** Plantable area, m². What the per-area rates are multiplied by. */
  total: number;
}

/**
 * The ground worth planting on: every triangle that is not too steep, with a
 * cumulative area table over it.
 *
 * Built once and sampled twice (trees, then grass). After two subdivision passes
 * the ground can be a quarter of a million triangles, and walking that twice to
 * compute the same table would be the slowest thing here.
 *
 * `positions` are flat viewer-space triangles (metres, Y-up) — the same arrays
 * the terrain parts carry, so this works for ground from either route into the
 * viewer without knowing anything about where it came from.
 */
function plantable(positions: Float32Array[]): Plantable | null {
  const cum: number[] = [];
  const tri: Array<{ p: Float32Array; i: number }> = [];
  let total = 0;

  const ax = new THREE.Vector3(), bx = new THREE.Vector3(), cx = new THREE.Vector3();
  const e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();

  for (const p of positions) {
    for (let i = 0; i + 8 < p.length; i += 9) {
      ax.set(p[i], p[i + 1], p[i + 2]);
      bx.set(p[i + 3], p[i + 4], p[i + 5]);
      cx.set(p[i + 6], p[i + 7], p[i + 8]);
      e1.subVectors(bx, ax);
      e2.subVectors(cx, ax);
      n.crossVectors(e1, e2);
      const area = n.length() / 2;
      if (area < 1e-6) continue;
      if (Math.abs(n.y / (area * 2)) < MAX_SLOPE_COS) continue;   // too steep
      total += area;
      cum.push(total);
      tri.push({ p, i });
    }
  }
  return tri.length && total > 0 ? { cum, tri, total } : null;
}

/** Pick `count` points on that ground, area-weighted. */
function scatter(surface: Plantable, count: number, rand: () => number): Spot[] {
  const { cum, tri, total } = surface;
  const out: Spot[] = [];
  for (let k = 0; k < count; k++) {
    const target = rand() * total;
    let lo = 0, hi = cum.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] < target) lo = mid + 1; else hi = mid;
    }
    const { p, i } = tri[lo];

    // Uniform barycentric sample — the sqrt is what keeps points off the corners.
    let u = rand(), v = rand();
    const su = Math.sqrt(u);
    u = 1 - su;
    v = v * su;
    const w = 1 - u - v;
    out.push({
      x: p[i] * u + p[i + 3] * v + p[i + 6] * w,
      y: p[i + 1] * u + p[i + 4] * v + p[i + 7] * w,
      z: p[i + 2] * u + p[i + 5] * v + p[i + 8] * w,
    });
  }
  return out;
}

/**
 * Sky for a planted site, and the horizon tone its fog has to match.
 *
 * The viewport's own background is a dusk gradient in dark mode and a flat page
 * colour in light mode — right for a bridge on a neutral ground, wrong the moment
 * the scene is grass, trees and soil, which read as being outdoors in daylight.
 *
 * Two variants rather than one: the app has a dark mode, and a bright blue sky
 * inside an otherwise dark shell glares. Dusk keeps the same horizon-anchored
 * look at the same brightness as the rest of the UI.
 *
 * The horizon colour is exported alongside because scene fog MUST be set to it.
 * Fog fades distant ground toward the fog colour, so leaving it on the default
 * white makes the far side of the terrain dissolve into a pale band that has
 * nothing to do with the sky behind it.
 */
const SKY = {
  day: { top: '#6ba4d8', mid: '#a8c9e4', horizon: '#dfe9f0' },
  dusk: { top: '#3d5878', mid: '#4d6076', horizon: '#6b7683' },
} as const;

export function skyHorizonHex(dark: boolean): number {
  return parseInt((dark ? SKY.dusk : SKY.day).horizon.slice(1), 16);
}

const skyCache = new Map<string, THREE.Texture>();

/** Vertical gradient background for a planted site. Cached per variant. */
export function skyBackground(dark: boolean): THREE.Texture {
  const key = dark ? 'dusk' : 'day';
  const hit = skyCache.get(key);
  if (hit) return hit;

  const tones = dark ? SKY.dusk : SKY.day;
  const c = document.createElement('canvas');
  c.width = 2;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 0, 512);
  grad.addColorStop(0, tones.top);
  grad.addColorStop(0.55, tones.mid);
  grad.addColorStop(1, tones.horizon);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 2, 512);

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  skyCache.set(key, tex);
  return tex;
}

/** A grass blade cluster drawn on a canvas, used as the tufts' alpha map. */
let grassTexture: THREE.Texture | null = null;
function tuftTexture(): THREE.Texture {
  if (grassTexture) return grassTexture;
  const s = 64;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, s, s);
  const r = rng(7);
  for (let i = 0; i < 14; i++) {
    const x = 6 + r() * (s - 12);
    const h = s * (0.45 + r() * 0.5);
    const lean = (r() - 0.5) * 14;
    ctx.strokeStyle = `rgba(${90 + r() * 40 | 0},${130 + r() * 60 | 0},${60 + r() * 40 | 0},1)`;
    ctx.lineWidth = 1.4 + r() * 1.4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, s);
    ctx.quadraticCurveTo(x + lean * 0.5, s - h * 0.6, x + lean, s - h);
    ctx.stroke();
  }
  grassTexture = new THREE.CanvasTexture(c);
  grassTexture.colorSpace = THREE.SRGBColorSpace;
  return grassTexture;
}

/**
 * ez-tree's own material, rebuilt as one that survives instancing.
 *
 * Its leaf material carries an `onBeforeCompile` that adds wind sway — simplex
 * noise displacing the vertices against a `uTime` uniform. The patch is not
 * instancing-aware, so with an InstancedMesh every leaf collapses to a single
 * unscaled canopy at the origin while the trunks scatter correctly: bare trees,
 * and one giant bush in the middle of the site.
 *
 * The sway is decorative and would need a per-frame uniform update we do not run,
 * so the fix is to keep the texture and drop the shader. Both meshes are rebuilt
 * as MeshStandardMaterial while we are here, so trees light like everything else
 * in the scene rather than sitting under Phong next to the bridge's PBR.
 *
 * The texture is passed through by reference: ez-tree loads it asynchronously
 * from a base64 URL, and it will appear by itself on a later frame.
 */
function instanceableMaterial(src: THREE.Material): THREE.Material {
  const m = src as THREE.MeshPhongMaterial;
  return new THREE.MeshStandardMaterial({
    map: m.map ?? null,
    color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
    alphaTest: m.alphaTest ?? 0,
    side: m.side ?? THREE.FrontSide,
    transparent: false,
    roughness: 0.92,
    metalness: 0,
  });
}

/** One instanced-able piece of a tree: ez-tree emits a trunk mesh and a leaf
 *  mesh per preset, and each becomes its own InstancedMesh. */
interface TreePiece {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  scale: number;
}

/** The tree variants, generated once and reused for every scene. Building one is
 *  20–50 ms, and they never change. */
let treeVariants: TreePiece[] | null = null;

function variants(): TreePiece[] {
  if (treeVariants) return treeVariants;
  const built: TreePiece[] = [];
  for (const preset of TREE_PRESETS) {
    let group: THREE.Object3D;
    try {
      const t = new Tree();
      t.loadPreset(preset);
      t.generate();
      group = t as unknown as THREE.Object3D;
    } catch (e) {
      console.warn('[Vegetation] preset failed, skipped:', preset, e);
      continue;
    }
    // Scale so the tallest point lands on TREE_HEIGHT_M; the presets are ~50–77
    // units and the scene is metres.
    const box = new THREE.Box3().setFromObject(group);
    const h = Math.max(1e-3, box.max.y - box.min.y);
    const scale = TREE_HEIGHT_M / h;
    group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const geom = m.geometry.clone();
      // Sit the trunk base on y = 0 so an instance's matrix is just "put it here".
      geom.translate(0, -box.min.y, 0);
      built.push({ geometry: geom, material: instanceableMaterial(m.material as THREE.Material), scale });
    });
  }
  treeVariants = built;
  return built;
}

/**
 * Build the planting for a set of ground triangles. Returns null when there is
 * nothing to plant or the density is zero.
 */
export function buildVegetation(
  ground: Float32Array[], opts: VegetationOptions,
): THREE.Group | null {
  const density = Math.max(0, Math.min(1, opts.density));
  if (density <= 0 || !ground.length) return null;

  const group = new THREE.Group();
  group.name = 'Vegetation';
  // Scenery sits under everything else in the render order and never writes to
  // the picking path — see the header.
  group.userData.noPick = true;

  const rand = rng(0x5eed);

  const surface = plantable(ground);
  if (!surface) return null;

  const budget = (perM2: number, cap: number) =>
    Math.min(cap, Math.round(surface.total * perM2 * density));

  // ---- grass ------------------------------------------------------------
  const tufts = scatter(surface, budget(GRASS_PER_M2, MAX_GRASS), rand);
  if (tufts.length) {
    // Two crossed quads: cheap, and reads as a tuft from any bearing without the
    // spinning that a camera-facing billboard shows when you orbit.
    const quad = new THREE.PlaneGeometry(GRASS_W, GRASS_H);
    quad.translate(0, GRASS_H / 2, 0);
    const quad2 = quad.clone();
    quad2.rotateY(Math.PI / 2);
    const blade = mergeTwo(quad, quad2);
    quad.dispose();
    quad2.dispose();

    const mat = new THREE.MeshStandardMaterial({
      map: tuftTexture(),
      alphaTest: 0.35,
      side: THREE.DoubleSide,
      roughness: 1,
      metalness: 0,
    });
    const mesh = new THREE.InstancedMesh(blade, mat, tufts.length);
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    for (let i = 0; i < tufts.length; i++) {
      const t = tufts[i];
      pos.set(t.x, t.y, t.z);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rand() * Math.PI * 2);
      const s = 0.7 + rand() * 0.8;
      scl.set(s, s, s);
      mesh.setMatrixAt(i, m.compose(pos, q, scl));
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  }

  // ---- trees ------------------------------------------------------------
  const spots = scatter(surface, budget(TREES_PER_M2, MAX_TREES), rand);
  const vs = spots.length ? variants() : [];
  if (vs.length && spots.length) {
    // Each variant carries its own slice of the spots, so all three species are
    // mixed through the planting rather than grouped.
    const perVariant: Spot[][] = vs.map((): Spot[] => []);
    // Variants come in pairs (trunk + leaves) per preset, so step by preset.
    const meshesPerTree = Math.max(1, Math.round(vs.length / TREE_PRESETS.length));
    const presetCount = Math.max(1, Math.round(vs.length / meshesPerTree));
    for (const s of spots) {
      const pick = Math.floor(rand() * presetCount);
      for (let m = 0; m < meshesPerTree; m++) {
        const idx = pick * meshesPerTree + m;
        if (idx < perVariant.length) perVariant[idx].push(s);
      }
    }

    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const pos = new THREE.Vector3();
    const scl = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);

    for (let v = 0; v < vs.length; v++) {
      const list = perVariant[v];
      if (!list.length) continue;
      const { geometry, material, scale } = vs[v];
      const mesh = new THREE.InstancedMesh(geometry, material, list.length);
      // Same seed sequence per variant so a tree's trunk and its leaves get the
      // same size and spin — they are separate instanced meshes at the same spot.
      const r2 = rng(0xa11ce + v - (v % meshesPerTree));
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        pos.set(t.x, t.y, t.z);
        q.setFromAxisAngle(up, r2() * Math.PI * 2);
        const s = scale * (1 - TREE_HEIGHT_VARIATION / 2 + r2() * TREE_HEIGHT_VARIATION);
        scl.set(s, s, s);
        mesh.setMatrixAt(i, m.compose(pos, q, scl));
      }
      mesh.instanceMatrix.needsUpdate = true;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      group.add(mesh);
    }
  }

  return group.children.length ? group : null;
}

/** Concatenate two small non-indexed-or-indexed geometries without pulling in
 *  BufferGeometryUtils for one call. Positions, normals and uvs only — that is
 *  all a PlaneGeometry has. */
function mergeTwo(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const an = a.toNonIndexed();
  const bn = b.toNonIndexed();
  const out = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'uv']) {
    const aa = an.getAttribute(name) as THREE.BufferAttribute | undefined;
    const ba = bn.getAttribute(name) as THREE.BufferAttribute | undefined;
    if (!aa || !ba) continue;
    const merged = new Float32Array(aa.array.length + ba.array.length);
    merged.set(aa.array as Float32Array, 0);
    merged.set(ba.array as Float32Array, aa.array.length);
    out.setAttribute(name, new THREE.BufferAttribute(merged, aa.itemSize));
  }
  an.dispose();
  bn.dispose();
  return out;
}

/**
 * True while any tree texture is still on its way.
 *
 * ez-tree loads its bark and leaf maps asynchronously from base64 URLs. Until the
 * leaf map lands its sampler returns nothing and `alphaTest: 0.5` discards every
 * fragment — so the trees render as bare sticks, while the trunks (alphaTest 0)
 * still show in their flat base colour. That is the whole symptom.
 *
 * It has to be POLLED rather than hooked: TextureLoader leaves `texture.image`
 * undefined until the load resolves, so at build time there is no <img> to attach
 * a listener to. Both halves matter — `image` appearing, and then `complete` /
 * `naturalWidth` confirming it actually decoded.
 */
export function foliagePending(): boolean {
  for (const v of treeVariants ?? []) {
    const map = (v.material as THREE.MeshStandardMaterial).map;
    if (!map) continue;
    const img = map.image as HTMLImageElement | undefined;
    if (!img) return true;
    if (typeof img.complete === 'boolean' && !img.complete) return true;
    if (typeof img.naturalWidth === 'number' && img.naturalWidth === 0) return true;
  }
  return false;
}

/** Tell three to upload the textures now that they are real. */
export function refreshFoliageTextures(): void {
  for (const v of treeVariants ?? []) {
    const map = (v.material as THREE.MeshStandardMaterial).map;
    if (map) map.needsUpdate = true;
  }
}

/** Free a planting's own geometries. The tree geometries and materials are the
 *  shared variants and are deliberately NOT disposed — they outlive any one
 *  scene and rebuilding them costs 20–50 ms each. */
export function disposeVegetation(group: THREE.Group): void {
  const shared = new Set((treeVariants ?? []).map((v) => v.geometry));
  group.traverse((o) => {
    const m = o as THREE.InstancedMesh;
    if (!m.isInstancedMesh) return;
    if (!shared.has(m.geometry)) m.geometry.dispose();
    m.dispose();
  });
}
